import { zonedDateTimeToUtc } from "services/date-converter";

// Field definition for <GlobalAdvanceFilter fields={[...]} />. The page decides
// which columns are filterable and how each one is edited:
//
//   {
//     key: "company_fid",            // column sent in `search` — the backend whitelists it
//     label: "Company",
//     type: "select",                // see FILTER TYPES below
//     placeholder: "All",            // optional
//     group: "Organization",         // optional section heading in the drawer (icon from FilterSection.jsx)
//     hint: "Company the user belongs to", // optional ⓘ tooltip beside the label
//   }
//
// FILTER TYPES (and the value each one sends in `search`):
//
//   select    dropdown from the page's `dependenciesLoader` (same idea as EntityFormModal):
//               optionsKey: "companies", optionLabel: "company_name" (dot path ok),
//               optionValue: "id" (default), optionLabelFormatter: (row) => string
//             -> [1, 2]  (or one value with `single: true`)
//   enum      dropdown of fixed values given by the page:
//               values: ["DRAFT", "PUBLISHED"]  or  options: [{ value, label }]
//             -> ["DRAFT"]  (or one value with `single: true`)
//   boolean   Any / Yes / No — trueLabel, falseLabel rename the two choices
//             -> "Yes" | "No"
//   date      From / To calendar days, for date-only columns (@db.Date)
//             -> { from: "2026-10-01", to: "2026-10-31" }
//   datetime  From / To calendar days, for timestamp columns; whole days in the account timezone.
//               showTime: true adds a time of day (From defaults to 00:00, To to 23:59, so
//               picking only days still covers them whole) — use it where the hour matters,
//               e.g. last login
//             -> { from: "<UTC ISO>", to: "<UTC ISO>" }
//   time      From / To time of day
//             -> { from: "09:00", to: "17:30" }
//   int | float | decimal   number input, digits only (float/decimal allow a decimal point)
//             -> 42 | 4.5
//   string    text input, trimmed; the backend matches it as "contains"
//             -> "acme"
//
// Either side of a range may be left empty.

export const RANGE_TYPES = new Set(["date", "datetime", "time"]);
export const NUMBER_TYPES = new Set(["int", "float", "decimal"]);
const LIST_TYPES = new Set(["select", "enum"]);

// Ready-made Active / Inactive filter for any table with an `is_activated` column.
export const STATUS_FIELD = {
  key: "is_activated",
  label: "Status",
  type: "boolean",
  trueLabel: "Active",
  falseLabel: "Inactive",
};

// Options from a list of enum values, e.g. ["DRAFT", "PUBLISHED"] -> "Draft", "Published".
export const enumOptions = (values) =>
  values.map((value) => ({
    value,
    label: String(value)
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
  }));

const resolvePath = (obj, path) => path?.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);

// Options shown for a list field: fixed ones from the page, or rows from the
// dependencies loader (`optionsKey`).
export const resolveFieldOptions = (field, dependencies = {}) => {
  if (field.options) return field.options;
  if (field.type === "enum" && field.values) return enumOptions(field.values);
  if (field.optionsKey) {
    return (dependencies[field.optionsKey] || []).map((row) => ({
      value: resolvePath(row, field.optionValue || "id"),
      label: field.optionLabelFormatter ? field.optionLabelFormatter(row) : resolvePath(row, field.optionLabel),
    }));
  }
  return [];
};

const NUMBER_PATTERNS = {
  int: /^-?\d*$/,
  float: /^-?\d*\.?\d*$/,
  decimal: /^-?\d*\.?\d*$/,
};

// Whether a keystroke leaves the number input in a valid (possibly partial) state.
export const isNumberInput = (type, text) => NUMBER_PATTERNS[type].test(text);

const parseNumber = (text) => {
  if (text === undefined || text === null || String(text).trim() === "") return null;
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
};

export const hasValue = (field, value) => {
  if (RANGE_TYPES.has(field.type)) return !!(value?.from || value?.to);
  if (LIST_TYPES.has(field.type)) return Array.isArray(value) && value.length > 0;
  if (field.type === "boolean") return value === "Yes" || value === "No";
  if (NUMBER_TYPES.has(field.type)) return parseNumber(value) !== null;
  if (field.type === "string") return typeof value === "string" && value.trim() !== "";
  return false;
};

export const countActive = (values, fields) => fields.filter((field) => hasValue(field, values[field.key])).length;

// The pickers carry a time of day too; only the calendar day matters here.
const dayKey = (date) => date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate();

// Returns an error message when a range's From is after its To.
export const rangeError = (field, value) => {
  if (!RANGE_TYPES.has(field.type) || !value?.from || !value?.to) return null;
  const inverted =
    field.type === "time" || field.showTime ? value.from > value.to : dayKey(value.from) > dayKey(value.to);
  return inverted ? `From ${field.type === "time" ? "time" : "date"} must be on or before To.` : null;
};

// The picked calendar day as an exact UTC instant at the start (or end) of
// that day in the account timezone, so "1 Oct" matches the same day the
// table's LocalDateTime column shows.
export const toUtcBoundary = (date, timeZone, endOfDay = false) => {
  if (!date) return "";
  const start = zonedDateTimeToUtc(date.getFullYear(), date.getMonth() + 1, date.getDate(), 0, 0, timeZone);
  if (!endOfDay) return start.toISOString();
  const nextDay = zonedDateTimeToUtc(date.getFullYear(), date.getMonth() + 1, date.getDate() + 1, 0, 0, timeZone);
  return new Date(nextDay.getTime() - 1).toISOString();
};

// The picked date and time as an exact UTC instant in the account timezone.
// A "To" bound covers its whole minute, so 23:59 reaches the end of the day.
const toUtcInstant = (date, timeZone, endOfMinute = false) => {
  if (!date) return "";
  const start = zonedDateTimeToUtc(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    timeZone
  );
  return new Date(start.getTime() + (endOfMinute ? 59999 : 0)).toISOString();
};

const pad2 = (n) => String(n).padStart(2, "0");
const toDateOnly = (date) => (date ? `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}` : "");

const serializeRange = (field, { from, to }, timeZone) => {
  if (field.type === "date") return { from: toDateOnly(from), to: toDateOnly(to) };
  if (field.type === "datetime" && field.showTime) {
    return { from: toUtcInstant(from, timeZone), to: toUtcInstant(to, timeZone, true) };
  }
  if (field.type === "datetime") return { from: toUtcBoundary(from, timeZone), to: toUtcBoundary(to, timeZone, true) };
  return { from: from || "", to: to || "" };
};

// Wire format of one field's value (see FILTER TYPES above).
const serializeValue = (field, value, timeZone) => {
  if (RANGE_TYPES.has(field.type)) {
    const range = serializeRange(field, value, timeZone);
    return Object.fromEntries(Object.entries(range).filter(([, bound]) => bound));
  }
  if (LIST_TYPES.has(field.type)) return field.single ? value[0] : value;
  if (NUMBER_TYPES.has(field.type)) return parseNumber(value);
  if (field.type === "string") return value.trim();
  return value;
};

// What onApply receives: `search`, the object the page posts to its get-all API.
export const buildFilterPayload = (values, fields, timeZone) => {
  const search = {};
  for (const field of fields) {
    const value = values[field.key];
    if (hasValue(field, value)) search[field.key] = serializeValue(field, value, timeZone);
  }
  return { search };
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatDay = (date) => `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
const formatClock = (date, timeFormat) => {
  const hour = date.getHours();
  const minute = pad2(date.getMinutes());
  return timeFormat === "12" ? `${hour % 12 || 12}:${minute} ${hour < 12 ? "AM" : "PM"}` : `${pad2(hour)}:${minute}`;
};

// Short text shown on a field's row so the active filters read at a glance.
// `timeFormat` is the account's "12" / "24" hour preference.
export const describeValue = (field, value, options = [], timeFormat = "24") => {
  if (!hasValue(field, value)) return "";
  if (RANGE_TYPES.has(field.type)) {
    let format = formatDay;
    if (field.type === "time") format = (time) => time;
    else if (field.showTime) format = (date) => `${formatDay(date)}, ${formatClock(date, timeFormat)}`;
    if (value.from && value.to) return `${format(value.from)} – ${format(value.to)}`;
    return value.from ? `From ${format(value.from)}` : `Until ${format(value.to)}`;
  }
  if (LIST_TYPES.has(field.type)) {
    if (value.length > 1) return `${value.length} selected`;
    const match = options.find((option) => String(option.value) === String(value[0]));
    return match?.label ?? "1 selected";
  }
  if (field.type === "boolean") return value === "Yes" ? field.trueLabel || "Yes" : field.falseLabel || "No";
  if (NUMBER_TYPES.has(field.type)) return `= ${parseNumber(value)}`;
  return `Contains "${value.trim()}"`;
};
